/**
 * TutorScopeContext — the single source of truth for the app-level AI Tutor.
 *
 * Every page in the app can:
 *   • push a scope   → `setScope({ module, topic })`
 *   • push a message → `pushAsk(text)`  (auto-opens docked pane)
 *   • flip the pane  → `setTutorState('bubble' | 'docked' | 'float')`
 *
 * The launcher (mounted once in App.js) reads all of this state and renders
 * the tutor bubble / docked pane / floating window. No component below App
 * should ever render another tutor UI — this context IS the tutor.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const TUTOR_W_KEY = 'console.tutorWidth';

const TutorScopeContext = createContext(null);

export function TutorScopeProvider({ children }) {
  const [currentModule, setCurrentModule] = useState(null);
  const [currentTopic, setCurrentTopic]   = useState(null);
  const [tutorState, setTutorState] = useState('bubble'); // bubble | docked | float
  const [messages, setMessages]     = useState([]);
  const [tutorPaneW, setTutorPaneW] = useState(() => {
    if (typeof window === 'undefined') return 460;
    const v = parseInt(window.localStorage.getItem(TUTOR_W_KEY) || '460', 10);
    return Number.isFinite(v) ? Math.min(900, Math.max(320, v)) : 460;
  });
  const [bubblePos, setBubblePos] = useState({ x: 24, y: 24 });

  const setScope = useCallback((next) => {
    setCurrentModule(next?.module || null);
    setCurrentTopic(next?.topic || null);
  }, []);

  // Seed a user question into the convo and pop the docked pane.
  // Actual /api/chat call happens when the launcher's convo effect sees
  // a message with `pending:true` — keeps the API surface tiny.
  const pushAsk = useCallback((text) => {
    const q = (text || '').trim();
    if (!q) return;
    setMessages((prev) => [...prev, { role: 'user', text: q, pending: true }]);
    setTutorState('docked');
  }, []);

  const value = useMemo(() => ({
    currentModule, currentTopic, setScope,
    tutorState, setTutorState,
    messages, setMessages,
    tutorPaneW, setTutorPaneW,
    bubblePos, setBubblePos,
    pushAsk,
  }), [
    currentModule, currentTopic, setScope,
    tutorState, messages, tutorPaneW, bubblePos, pushAsk,
  ]);

  return (
    <TutorScopeContext.Provider value={value}>{children}</TutorScopeContext.Provider>
  );
}

export function useTutorScope() {
  const ctx = useContext(TutorScopeContext);
  if (!ctx) {
    // Safe no-op fallback — pages that mount outside the provider can still
    // call these methods without exploding (e.g. Login / Register).
    return {
      currentModule: null, currentTopic: null,
      setScope: () => {},
      tutorState: 'bubble', setTutorState: () => {},
      messages: [], setMessages: () => {},
      tutorPaneW: 460, setTutorPaneW: () => {},
      bubblePos: { x: 24, y: 24 }, setBubblePos: () => {},
      pushAsk: () => {},
    };
  }
  return ctx;
}
